/**
 * User-Scoped Storage Utility
 *
 * Problem: In multi-user environments (like Telegram Mini Apps with account switching),
 * different users can share the same browser storage, causing data leakage.
 *
 * Solution: Namespace all user-specific storage by user ID.
 * Use `userStorage` instead of `localStorage` for user-specific data.
 */

import { del } from 'idb-keyval';

const USER_ID_KEY = 'current_user_id';
const LEGACY_CACHE_KEY = 'REACT_QUERY_OFFLINE_CACHE';
const USER_STORAGE_PREFIX = 'user:';

/**
 * Get user ID from platform-specific sources.
 * Currently supports: Telegram WebApp SDK
 * Returns null if no platform user ID is available.
 */
export function getPlatformUserId(): string | null {
    // Telegram Mini App
    try {
        const telegramUserId = window.Telegram?.WebApp?.initDataUnsafe?.user?.id;
        if (telegramUserId) return String(telegramUserId);
    } catch { /* ignore */ }

    // Add other platforms here as needed (e.g., native app user ID)

    return null;
}

/**
 * Get the stored user ID from localStorage
 */
export function getStoredUserId(): string | null {
    return localStorage.getItem(USER_ID_KEY);
}

/**
 * Get the effective user ID (platform > stored > 'default')
 */
export function getEffectiveUserId(): string {
    return getPlatformUserId() || getStoredUserId() || 'default';
}

/**
 * Store the current user ID
 */
export function storeUserId(userId: string): void {
    localStorage.setItem(USER_ID_KEY, userId);
}

/**
 * Get the namespaced cache key for IndexedDB (React Query)
 */
export function getCacheKey(userId?: string): string {
    return `REACT_QUERY_OFFLINE_CACHE:${userId || getEffectiveUserId()}`;
}

/**
 * User-namespaced storage wrapper.
 * Use this instead of localStorage for user-specific data.
 * Keys are automatically prefixed with the user ID.
 */
export const userStorage = {
    _getKey(key: string): string {
        return `${USER_STORAGE_PREFIX}${getEffectiveUserId()}:${key}`;
    },

    getItem(key: string): string | null {
        return localStorage.getItem(this._getKey(key));
    },

    setItem(key: string, value: string): void {
        localStorage.setItem(this._getKey(key), value);
    },

    removeItem(key: string): void {
        localStorage.removeItem(this._getKey(key));
    },

    /**
     * Clear all storage for a specific user (or current user if not specified)
     */
    clearForUser(userId?: string): void {
        const prefix = `${USER_STORAGE_PREFIX}${userId || getEffectiveUserId()}:`;
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key?.startsWith(prefix)) {
                keysToRemove.push(key);
            }
        }

        keysToRemove.forEach(key => localStorage.removeItem(key));
    },
};

/**
 * Clear only the persisted React Query cache (IndexedDB) for a user.
 * Keeps auth token / flags — use this for crash recovery, not for logout.
 */
export async function clearQueryCache(userId?: string): Promise<void> {
    const targetUserId = userId || getEffectiveUserId();
    await Promise.all([
        del(LEGACY_CACHE_KEY).catch(() => {}),
        del(getCacheKey(targetUserId)).catch(() => {}),
    ]);
}

/**
 * Clear all data for a user (localStorage incl. auth token + IndexedDB cache).
 * Logout / account switch only — never call this for error recovery.
 */
export async function clearUserData(userId?: string): Promise<void> {
    const targetUserId = userId || getEffectiveUserId();
    console.log('[UserStorage] Clearing data for user:', targetUserId);

    // Clear user-namespaced localStorage
    userStorage.clearForUser(targetUserId);

    await clearQueryCache(targetUserId);

    sessionStorage.clear();
}

/**
 * Check for user change. Returns info about the change.
 */
export function checkUserChange(): { changed: boolean; oldUserId?: string; newUserId?: string } {
    const currentId = getPlatformUserId();
    const storedId = getStoredUserId();

    if (!currentId) {
        return { changed: false };
    }

    if (!storedId) {
        storeUserId(currentId);
        return { changed: false };
    }

    if (currentId !== storedId) {
        return { changed: true, oldUserId: storedId, newUserId: currentId };
    }

    return { changed: false };
}

// Backwards compatibility alias
export const getTelegramUserId = getPlatformUserId;
